import { useEffect, useState } from 'react';
import {
    Block,
    BlocksContainer,
    Container,
    StatusText,
} from './RangeBlocks.styles';
import {
    isValidString,
    createScoreRangeConfig,
    IRangeBlocksValues,
} from '../../../../common';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface Props {
    externalValue: IRangeBlocksValues;
    secondaryExternalValue?: IRangeBlocksValues;
    onChange?: (value: IRangeBlocksValues) => void;
    disabled?: boolean;
    fillColor?: string;
    secondaryFillColor?: string;
    backgroundColor?: string;
    showSelectionText?: boolean;
    scoreRangeConfig?: ReturnType<typeof createScoreRangeConfig>;
    selectMaxOnly?: boolean;
    useInternalValue?: boolean;
}

const RangeBlocks = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const {
        externalValue,
        secondaryExternalValue,
        onChange,
        disabled = false,
        fillColor = theme.palette.success.main,
        backgroundColor = theme.palette.grey[300],
        showSelectionText = true,
        scoreRangeConfig,
        selectMaxOnly = false,
        secondaryFillColor = theme.palette.grey[300],
        useInternalValue = true,
    } = props;

    const [internalValue, setInternalValue] =
        useState<IRangeBlocksValues | null>(externalValue);
    const [clickCount, setClickCount] = useState(0);

    useEffect(() => {
        setInternalValue(externalValue);
    }, [externalValue]);

    const value = internalValue || { from: 0, to: 0 };

    const getStatusText = (
        value: IRangeBlocksValues,
        scoreRangeConfig: ReturnType<typeof createScoreRangeConfig>
    ) => {
        if (selectMaxOnly) {
            return `${scoreRangeConfig.getLabel(value.to, value.to)}`;
        }
        return `${t('components.Range')}: ${scoreRangeConfig.getLabel(value.from, value.from)}-${scoreRangeConfig.getLabel(value.to, value.to)}`;
    };

    const handleBlockClick = (blockIndex: number) => {
        if (disabled) return;

        const clickedValue = blockIndex * 2 + 1;

        if (selectMaxOnly) {
            // In selectMaxOnly mode, always set from=0 and to=clickedValue+1
            const newValue = { from: 0, to: clickedValue + 1 };
            useInternalValue && setInternalValue(newValue);
            onChange?.(newValue);
            return;
        }

        setClickCount((prevCount) => {
            const newCount = (prevCount + 1) % 2;

            if (newCount === 1) {
                // First click: set the clicked value as both from and to
                const newValue = { from: clickedValue, to: clickedValue + 1 };
                useInternalValue && setInternalValue(newValue);
                onChange?.(newValue);
            } else {
                // Second click: determine the range
                if (useInternalValue) {
                    setInternalValue((prev) => {
                        if (!prev) return null;
                        const newFrom = Math.min(prev.from, clickedValue);
                        const newTo = Math.max(prev.to, clickedValue + 1);
                        const newValue = { from: newFrom, to: newTo };
                        onChange?.(newValue);
                        return newValue;
                    });
                } else if (externalValue) {
                    const newFrom = Math.min(externalValue.from, clickedValue);
                    const newTo = Math.max(externalValue.to, clickedValue + 1);
                    const newValue = { from: newFrom, to: newTo };
                    onChange?.(newValue);
                }
            }

            return newCount;
        });
    };

    const isBlockSelected = (blockIndex: number) => {
        if (!internalValue) return false;
        const blockStart = blockIndex * 2 + 1;
        const blockEnd = blockStart + 1;
        return blockStart <= internalValue.to && blockEnd >= internalValue.from;
    };

    const isBlockSecondarySelected = (blockIndex: number) => {
        if (!secondaryExternalValue) return false;
        const blockStart = blockIndex * 2 + 1;
        const blockEnd = blockStart + 1;
        return (
            blockStart <= secondaryExternalValue.to &&
            blockEnd >= secondaryExternalValue.from
        );
    };

    const selectionText = (() => {
        if (selectMaxOnly) {
            return `${scoreRangeConfig?.getLabel(value.to, value.to)}`;
        }
        if (
            !value ||
            (typeof value.from === 'undefined' &&
                typeof value.to === 'undefined')
        ) {
            return `${t('components.Score')}: `;
        }

        const fromLabel =
            value.from !== 0
                ? scoreRangeConfig?.getLabel(value.from, value.from)
                : '';
        const toLabel =
            value.to !== 0
                ? scoreRangeConfig?.getLabel(value.to, value.to)
                : '';

        if (clickCount === 1) {
            return `${t('components.SelectingRange')}: ${fromLabel} - ${fromLabel}`;
        }
        if (isValidString(fromLabel) && isValidString(toLabel)) {
            return `${t('components.SelectedRange')}: ${fromLabel} - ${toLabel}`;
        }
        return `${t('components.SelectedRange')}:`;
    })();

    return (
        <Container>
            <BlocksContainer role="group" aria-label="Range selector">
                {[...Array(5)].map((_, index) => (
                    <Block
                        key={index}
                        isSelected={isBlockSelected(index)}
                        isSecondarySelected={isBlockSecondarySelected(index)}
                        isDisabled={disabled}
                        fillColor={fillColor}
                        secondaryFillColor={secondaryFillColor}
                        backgroundColor={backgroundColor}
                        onClick={() => handleBlockClick(index)}
                        role="button"
                        tabIndex={disabled ? -1 : 0}
                        aria-label={`${t('components.SelectValue')} ${index * 2 + 1}-${index * 2 + 2}`}
                    />
                ))}
            </BlocksContainer>
            {showSelectionText && scoreRangeConfig && (
                <StatusText>
                    {disabled
                        ? getStatusText(value, scoreRangeConfig)
                        : selectionText}
                </StatusText>
            )}
        </Container>
    );
};

export default RangeBlocks;
